import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, ArrowLeft, ArrowRight, CalendarDays, Coins, Globe2, Hash, Keyboard, Plane, ScanLine, UserRound } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ApplicantFields, ExtractionNotice, PassportScanner, PhotoPicker } from '@/components/applicant';
import { PageHeader } from '@/components/layout/AppShell';
import { ScanViewer } from '@/components/scanViewer';
import { Page, Rise, TiltCard } from '@/components/motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useObjectUrl } from '@/components/ui/dropzone';
import { Field, Input, Select } from '@/components/ui/input';
import { checksApi, coinsApi } from '@/lib/api/endpoints';
import { isApiError, userMessage } from '@/lib/api/errors';
import type { PassportExtraction } from '@/lib/api/types';
import { EMPTY_APPLICANT, prefillFromExtraction, toApplicantIn, validateApplicant, type ApplicantErrors, type ApplicantForm } from '@/lib/applicant';
import { ME_KEY, useMe } from '@/lib/auth';
import { cn, isoToDdMmYyyy } from '@/lib/utils';

/** Portal nationality codes we know. Anything else can be entered by hand. */
const NATIONALITIES = [{ code: 317, name: 'ETHIOPIA' }] as const;
const OTHER = 'other';

type PassportErrors = Partial<Record<'passport_no' | 'passport_expiry' | 'nationality_code' | 'nationality_text', string>>;

function PassportPreview({
  passportNo,
  expiry,
  nationality,
  name,
  nameAr,
  photo,
}: {
  passportNo: string;
  expiry: string;
  nationality: string;
  name: string;
  nameAr: string;
  photo: File | null;
}) {
  const photoUrl = useObjectUrl(photo);
  const nat = (nationality || 'XXX').slice(0, 3).padEnd(3, '<');
  const nameMrz = name.trim().toUpperCase().replace(/[^A-Z ]/g, '').split(/\s+/).filter(Boolean);
  const surname = nameMrz.length > 1 ? nameMrz[nameMrz.length - 1] : '';
  const given = nameMrz.slice(0, -1).join('<');
  const mrz1 = `P<${nat}${surname}<<${given}${'<'.repeat(44)}`.slice(0, 44);
  const mrz2 = `${passportNo.padEnd(9, '<').slice(0, 9)}0${nat}${'<'.repeat(31)}`.slice(0, 44);
  return (
    <TiltCard>
      <div className="relative aspect-[1.42] w-full overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-950 p-5 text-white shadow-2xl ring-1 ring-white/10">
        <div className="absolute -right-10 -top-10 size-40 rounded-full bg-amber-300/20 blur-2xl" />
        <div className="absolute inset-0 opacity-[0.07] [background-image:repeating-linear-gradient(45deg,#fff_0_1px,transparent_1px_12px)]" />
        <div className="relative flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.25em] text-white/60">
          <span>Passport</span>
          <Plane className="size-4" />
        </div>
        <div className="relative mt-3 flex gap-4">
          <div className="flex aspect-[35/45] w-16 shrink-0 items-center justify-center overflow-hidden rounded-md bg-white/10 ring-1 ring-white/20">
            {photoUrl ? <img src={photoUrl} alt="" className="size-full object-cover" /> : <UserRound className="size-6 text-white/40" />}
          </div>
          <div className="grid min-w-0 flex-1 grid-cols-2 gap-x-3 gap-y-2">
            <div className="col-span-2 min-w-0">
              <p className="text-[9px] uppercase tracking-wider text-white/50">Name</p>
              <p className="truncate text-sm font-bold uppercase">{name || '—'}</p>
              {nameAr && <p className="truncate font-arabic text-sm text-white/80" dir="rtl">{nameAr}</p>}
            </div>
            <div className="min-w-0">
              <p className="text-[9px] uppercase tracking-wider text-white/50">Passport No.</p>
              <motion.p key={passportNo} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} className="truncate font-mono text-sm font-bold tracking-widest">
                {passportNo || '—'}
              </motion.p>
            </div>
            <div className="min-w-0">
              <p className="text-[9px] uppercase tracking-wider text-white/50">Expiry</p>
              <p className="font-mono text-sm font-semibold">{expiry || '—'}</p>
            </div>
            <div className="col-span-2 min-w-0">
              <p className="text-[9px] uppercase tracking-wider text-white/50">Nationality</p>
              <p className="truncate text-sm font-semibold">{nationality || '—'}</p>
            </div>
          </div>
        </div>
        <div className="absolute inset-x-5 bottom-3 space-y-0.5 overflow-hidden font-mono text-[9px] leading-tight tracking-[0.12em] text-white/50 sm:text-[10px]">
          <p className="truncate">{mrz1}</p>
          <p className="truncate">{mrz2}</p>
        </div>
      </div>
    </TiltCard>
  );
}

function StageTabs({ stage, manual }: { stage: 'scan' | 'review'; manual: boolean }) {
  const steps = [
    { key: 'scan', label: 'Scan passport', icon: ScanLine },
    manual ? { key: 'review', label: 'Enter by hand', icon: Keyboard } : { key: 'review', label: 'Review & submit', icon: UserRound },
  ] as const;
  return (
    <div className="mb-5 flex items-center gap-2">
      {steps.map((s, i) => {
        const active = s.key === stage;
        const done = stage === 'review' && s.key === 'scan';
        return (
          <React.Fragment key={s.key}>
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'flex size-8 items-center justify-center rounded-full text-sm transition-colors',
                  active && 'bg-brand-gradient text-primary-foreground shadow-glow',
                  done && 'bg-emerald-500 text-white',
                  !active && !done && 'bg-secondary text-muted-foreground',
                )}
              >
                <s.icon className="size-4" />
              </span>
              <span className={cn('text-sm font-semibold', !active && 'text-muted-foreground')}>{s.label}</span>
            </div>
            {i === 0 && (
              <div className="relative h-0.5 flex-1 overflow-hidden rounded-full bg-border">
                <motion.div className="absolute inset-y-0 left-0 bg-emerald-500" initial={false} animate={{ width: stage === 'review' ? '100%' : '0%' }} />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function NewCheckPage() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const pricing = useQuery({ queryKey: ['pricing'], queryFn: coinsApi.pricing, staleTime: 60_000 });
  const cost = pricing.data?.rules.check_cost_coins ?? 1;
  const short = !me.is_admin && me.coin_balance < cost;

  const [stage, setStage] = React.useState<'scan' | 'review'>('scan');
  const [extraction, setExtraction] = React.useState<PassportExtraction | null>(null);
  const [missing, setMissing] = React.useState<string[]>([]);
  const [passportNo, setPassportNo] = React.useState('');
  const [expiry, setExpiry] = React.useState('');
  const [nat, setNat] = React.useState<string>(String(NATIONALITIES[0].code));
  const [otherCode, setOtherCode] = React.useState('');
  const [otherName, setOtherName] = React.useState('');
  const [applicant, setApplicant] = React.useState<ApplicantForm>(EMPTY_APPLICANT);
  const [photo, setPhoto] = React.useState<File | null>(null);
  /** The scan the reader couldn't handle, shown large while entering by hand. */
  const [handScan, setHandScan] = React.useState<File | null>(null);
  const [errors, setErrors] = React.useState<PassportErrors>({});
  const [applicantErrors, setApplicantErrors] = React.useState<ApplicantErrors>({});

  const nationalityName = nat === OTHER ? otherName.trim().toUpperCase() : (NATIONALITIES.find((n) => String(n.code) === nat)?.name ?? '');

  function applyExtraction(x: PassportExtraction) {
    const p = prefillFromExtraction(x);
    setExtraction(x);
    setMissing(p.missing);
    setPassportNo(p.passport_no);
    setExpiry(p.passport_expiry);
    if (p.nationality_code && NATIONALITIES.some((n) => n.code === p.nationality_code)) {
      setNat(String(p.nationality_code));
    } else if (p.nationality_code || p.nationality_text) {
      setNat(OTHER);
      setOtherCode(p.nationality_code ? String(p.nationality_code) : '');
      setOtherName(p.nationality_text);
    }
    setApplicant(p.applicant);
    setHandScan(null);
    setStage('review');
  }

  function enterByHand(failedScan: File | null) {
    setExtraction(null);
    setMissing([]);
    setHandScan(failedScan);
    setStage('review');
  }

  function scanAgain() {
    setHandScan(null);
    setStage('scan');
  }

  // Entering by hand (scan skipped or unreadable): the form gets AI help with the Arabic
  const manual = stage === 'review' && !extraction;
  const withScan = manual && !!handScan;

  const create = useMutation({
    mutationFn: async (body: Parameters<typeof checksApi.create>[0]) => {
      const check = await checksApi.create(body);
      // The photo can only be attached once the check exists. A failed upload doesn't undo the check.
      if (photo) {
        try {
          return { check: await checksApi.uploadPhoto(check.id, photo), photoFailed: null };
        } catch (e) {
          return { check, photoFailed: userMessage(e) };
        }
      }
      return { check, photoFailed: null };
    },
    onSuccess: ({ check, photoFailed }) => {
      qc.invalidateQueries({ queryKey: ME_KEY });
      qc.invalidateQueries({ queryKey: ['checks'] });
      qc.setQueryData(['check', check.id], check);
      toast.success('Check started', { description: me.is_admin ? "No coins were used. We'll update this page live." : `${check.coins_charged} coin${check.coins_charged === 1 ? '' : 's'} used. We'll update this page live.` });
      if (photoFailed) toast.error("The photo wasn't saved", { description: `${photoFailed} You can add it on the check page.` });
      navigate(`/checks/${check.id}`);
    },
    onError: (err) => {
      if (isApiError(err, 402)) {
        toast.error('Not enough coins', { description: err.message, action: { label: 'Buy coins', onClick: () => navigate('/coins/buy') } });
      } else if (isApiError(err, 422)) {
        const fe = err.fieldErrors;
        setErrors(fe);
        setApplicantErrors(fe as ApplicantErrors);
        toast.error(err.message);
      } else {
        toast.error(userMessage(err, 'Could not start the check'));
      }
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const pn = passportNo.trim().toUpperCase();
    const local: PassportErrors = {};
    if (!/^[A-Z0-9]{5,20}$/.test(pn)) local.passport_no = '5–20 letters or digits.';
    if (!expiry) local.passport_expiry = 'Pick the expiry date from the passport.';
    const code = nat === OTHER ? Number(otherCode) : Number(nat);
    if (!Number.isInteger(code) || code <= 0) local.nationality_code = 'Enter the portal’s numeric nationality code.';
    if (nationalityName.length < 2 || nationalityName.length > 64) local.nationality_text = '2–64 characters.';
    const aErr = validateApplicant(applicant);
    if (photo && !applicant.full_name.trim()) aErr.full_name = 'Add the full name to go with the photo.';
    setErrors(local);
    setApplicantErrors(aErr);
    if (Object.keys(local).length || Object.keys(aErr).length) {
      toast.error('Please fix the highlighted fields');
      return;
    }
    create.mutate({
      passport_no: pn,
      passport_expiry: isoToDdMmYyyy(expiry),
      nationality_code: code,
      nationality_text: nationalityName,
      applicant: toApplicantIn(applicant),
    });
  }

  const flag = (k: string, v: string) => missing.includes(k) && !v;
  const flagCls = (on: boolean) => (on ? 'border-amber-400 ring-4 ring-amber-400/15' : '');
  const MISSING = "Couldn't read this from the scan. Please fill it in.";

  return (
    <Page>
      <PageHeader
        eyebrow="New check"
        title={withScan ? 'Enter the passport details' : 'Check a UAE visa'}
        description={
          withScan
            ? 'Read the details from your scan and type them in. The Arabic is written for you and checked by AI.'
            : 'Scan the passport to fill everything in, then review it before you start.'
        }
      />
      {/* One container for the scan and the form, so a pinned scan stays in view over the whole form */}
      <div className="space-y-6">
        {withScan && handScan && <ScanViewer file={handScan} />}
        <div className={cn('grid gap-6', withScan ? 'lg:grid-cols-[1fr_320px]' : 'lg:grid-cols-[1fr_380px]')}>
          <Rise>
            <Card>
              <CardContent className="pt-5 sm:pt-6">
                <StageTabs stage={stage} manual={manual} />
                <AnimatePresence mode="wait">
                  {stage === 'scan' ? (
                    <motion.div key="scan" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                      <PassportScanner onResult={applyExtraction} onSkip={enterByHand} />
                    </motion.div>
                  ) : (
                    <motion.form key="review" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} onSubmit={onSubmit} noValidate className="space-y-6">
                      {extraction && <ExtractionNotice extraction={extraction} />}

                      <section className="space-y-4">
                        <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Passport (used for the check)</h2>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <Field label="Passport number" htmlFor="passport_no" error={errors.passport_no} hint={flag('passport_no', passportNo) ? MISSING : undefined}>
                            <Input
                              id="passport_no"
                              icon={<Hash />}
                              placeholder="EQ2630496"
                              className={cn('font-mono uppercase tracking-widest', flagCls(flag('passport_no', passportNo)))}
                              value={passportNo}
                              onChange={(e) => {
                                setPassportNo(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''));
                                setErrors((x) => ({ ...x, passport_no: undefined }));
                              }}
                              maxLength={20}
                              aria-invalid={!!errors.passport_no}
                            />
                          </Field>
                          <Field label="Passport expiry date" htmlFor="passport_expiry" error={errors.passport_expiry} hint={flag('passport_expiry', expiry) ? MISSING : undefined}>
                            <Input
                              id="passport_expiry"
                              type="date"
                              icon={<CalendarDays />}
                              className={flagCls(flag('passport_expiry', expiry))}
                              value={expiry}
                              onChange={(e) => {
                                setExpiry(e.target.value);
                                setErrors((x) => ({ ...x, passport_expiry: undefined }));
                              }}
                              aria-invalid={!!errors.passport_expiry}
                            />
                          </Field>
                          <Field label="Nationality" htmlFor="nationality" error={nat !== OTHER ? errors.nationality_code : undefined} className="sm:col-span-2">
                            <Select id="nationality" value={nat} onChange={(e) => setNat(e.target.value)}>
                              {NATIONALITIES.map((n) => (
                                <option key={n.code} value={n.code}>
                                  {n.name.charAt(0) + n.name.slice(1).toLowerCase()} ({n.code})
                                </option>
                              ))}
                              <option value={OTHER}>Other…</option>
                            </Select>
                          </Field>
                        </div>
                        {nat === OTHER && (
                          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="grid gap-4 sm:grid-cols-[140px_1fr]">
                            <Field label="Portal code" htmlFor="other_code" error={errors.nationality_code}>
                              <Input id="other_code" inputMode="numeric" icon={<Hash />} value={otherCode} onChange={(e) => setOtherCode(e.target.value.replace(/\D/g, ''))} aria-invalid={!!errors.nationality_code} />
                            </Field>
                            <Field label="Country name" htmlFor="other_name" error={errors.nationality_text} hint="As named on the ICP portal.">
                              <Input id="other_name" icon={<Globe2 />} className="uppercase" value={otherName} onChange={(e) => setOtherName(e.target.value)} aria-invalid={!!errors.nationality_text} />
                            </Field>
                          </motion.div>
                        )}
                      </section>

                      <section className="space-y-4 border-t pt-6">
                        <div>
                          <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Applicant (for the status report)</h2>
                          <p className="mt-1 text-xs text-muted-foreground">Optional. You can also add or fix these later on the check page.</p>
                        </div>
                        <ApplicantFields value={applicant} onChange={setApplicant} errors={applicantErrors} flagged={missing} assist={manual} />
                        <PhotoPicker file={photo} onFile={setPhoto} />
                      </section>

                      <div className="flex flex-col gap-3 rounded-xl border bg-secondary/40 p-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3 text-sm">
                          <span className="flex size-9 items-center justify-center rounded-full bg-gradient-to-br from-amber-300 to-amber-500 text-amber-950">
                            <Coins className="size-4" />
                          </span>
                          <span>
                            <span className="block font-semibold">{me.is_admin ? 'Free for admins' : cost === 0 ? 'Free right now' : `Costs ${cost} coin${cost === 1 ? '' : 's'}`}</span>
                            <span className="block text-xs text-muted-foreground">{me.is_admin ? 'Admin checks do not use coins.' : `You have ${me.coin_balance}. Refunded if the check fails.`}</span>
                          </span>
                        </div>
                        {short && (
                          <Button asChild variant="outline">
                            <Link to="/coins/buy">
                              <Coins /> Buy coins
                            </Link>
                          </Button>
                        )}
                      </div>

                      {short && (
                        <p className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400">
                          <AlertCircle className="size-4" /> You need {cost - me.coin_balance} more coin{cost - me.coin_balance === 1 ? '' : 's'} for this check.
                        </p>
                      )}

                      <div className="flex gap-2">
                        <Button type="button" variant="outline" size="lg" onClick={scanAgain}>
                          <ArrowLeft /> <span className="hidden sm:inline">Scan again</span>
                        </Button>
                        <Button type="submit" variant="brand" size="lg" className="group flex-1" loading={create.isPending} disabled={short}>
                          Start check <ArrowRight className="transition-transform group-hover:translate-x-1" />
                        </Button>
                      </div>
                    </motion.form>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>
          </Rise>
          {/* Not sticky beside a scan: a pinned scan already holds the top of the screen */}
          <Rise className={cn('space-y-4 lg:self-start', !withScan && 'lg:sticky lg:top-20')}>
            <PassportPreview
              passportNo={passportNo}
              expiry={isoToDdMmYyyy(expiry)}
              nationality={nationalityName}
              name={applicant.full_name}
              nameAr={applicant.full_name_ar}
              photo={photo}
            />
            <p className="px-1 text-center text-xs text-muted-foreground">Live preview of what you've entered.</p>
          </Rise>
        </div>
      </div>
    </Page>
  );
}
